import { DeleteOutline } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, Tab, TextField, Theme, Typography, useMediaQuery } from '@mui/material';
import * as React from 'react';
import { useMobileApi } from '../../../Api/Mobile/useMobileApi';
import { InsertLogRecordRequest, MobileRecordsVovClient, MobileWealthDTO, MobileWealthVovClient, SetWealthToEmployeeRequest } from '../../../ApiOld/Api/ApiServerVov';
import { useSnackbar } from 'notistack';
import { SignatureComponent } from '@syncfusion/ej2-react-inputs';
import { convertBlobToBase64 } from '../../Shared/Files/ConvertBlobToBase64';
import BuildingsList from '../Shared/BuildingsList';
import { useNavigate, useParams } from 'react-router';
import { useEmployeeContext } from '../../../Contexts/Providers/EmployeeLayoutProvider';
import { useTranslation } from 'react-i18next';
import { ArrowBackIosNew } from '@mui/icons-material';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckIcon from '@mui/icons-material/Check';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { MobileWealthDetailDialog } from './MobileWealthDetailDialog';
import { useEffect, useState } from 'react';
import WealthToBorrowList from '../Shared/WealthToBorrowList';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';

export interface IMobileWealthBorrowProps { }

export default function MobileWealthBorrow(props: IMobileWealthBorrowProps) {
    const [tempDate, setTempDate] = React.useState<Date | null>(null);
    const { authorizedMobileEmployeeApi } = useMobileApi(MobileWealthVovClient);
    const { authorizedMobileEmployeeApi: recordsApi } = useMobileApi(MobileRecordsVovClient);
    const [borrowedWealthActual, setBorrowedWealthActual] = React.useState<MobileWealthDTO | null>(null);
    const [borrowedWealth, setBorrowedWealth] = React.useState<MobileWealthDTO[]>([]);
    const [signing, setSigning] = React.useState(false);
    const { companySubdomain, deviceId, deviceModel, versionWithBuild } = useEmployeeContext();
    const [selectingBuilding, setSelectingBuilding] = React.useState(false);
    const [selectedBuildingId, setSelectedBuildingId] = React.useState<number | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const signRef = React.useRef<SignatureComponent>(null);
    const [data, setData] = React.useState("");
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const params = useParams();
    const { t, i18n } = useTranslation();
    const { setModuleButton } = useEmployeeContext();
    const isMobileS = useMediaQuery((theme: Theme) => theme.breakpoints.down(321));
    const isMobileM = useMediaQuery((theme: Theme) => theme.breakpoints.between(322, 376));
    const isMobileL = useMediaQuery((theme: Theme) => theme.breakpoints.between(376, 426));
    const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.up(427));

    window.sendScannedCode = (code: string) => {
        recordsApi.insertLog(new InsertLogRecordRequest({
            action: '(MobileWealthBorrow) window.sendScannedCode: ' + code,
            deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
        }));
        setData(code);
    }

    const getWealthDetail = React.useCallback(async () => {
        if (borrowedWealth.find(x => x.code === data) === undefined) {
            const borrowedWealthData = await authorizedMobileEmployeeApi.wealthMobileDetail(data);
            if (borrowedWealthData.ok) {
                if (borrowedWealthData.wealth) {
                    setBorrowedWealthActual(borrowedWealthData.wealth);
                }
                setDetailOpen(true);
            } else {
                enqueueSnackbar(t(borrowedWealthData?.message ?? 'WealthWithThisCodeDoesNotExists'), { variant: 'error' });
            }
        } else {
            enqueueSnackbar(t('WealthAlreadyAdded'), { variant: 'info' });
        }
    }, [data, borrowedWealth, authorizedMobileEmployeeApi, enqueueSnackbar, t]);

    const borrow = React.useCallback(async () => {
        if (selectedBuildingId === null) return;
        const blob = signRef.current?.saveAsBlob();
        let signature = "";
        if (blob) signature = await convertBlobToBase64(blob);
        let payload = new SetWealthToEmployeeRequest({
            buildingId: selectedBuildingId,
            rentDateOrientationFrom: tempDate ?? undefined,
            signature: signature,
            wealths: borrowedWealth.map(item => new MobileWealthDTO(item))
        });
        recordsApi.insertLog(new InsertLogRecordRequest({
            action: '(MobileWealthBorrow) Calling wealthBorrow endpoint ' + JSON.stringify(payload),
            deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
        }));
        await authorizedMobileEmployeeApi
            .setWealthToEmployee(
                payload,
            )
            .then((res) => {
                recordsApi.insertLog(new InsertLogRecordRequest({
                    action: '(MobileWealthBorrow) Borrow wealth success',
                    deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
                }));
                setModuleButton(null);
                navigate('/' + params['lang'] + '/' + companySubdomain + '/employee/wealth');
            })
            .catch((err) => {
                recordsApi.insertLog(new InsertLogRecordRequest({
                    action: '(MobileWealthBorrow) Borrow wealth success ' + err.message,
                    deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
                }));
                enqueueSnackbar(t('Error') + ' ' + err.message, { variant: 'error' });
            });
    }, [selectedBuildingId, tempDate, borrowedWealth, recordsApi, deviceModel, deviceId, versionWithBuild, authorizedMobileEmployeeApi, setModuleButton, navigate, params, companySubdomain, enqueueSnackbar, t]);

    useEffect(() => {
        if (data !== "")
            getWealthDetail();
    }, [data, getWealthDetail]);

    const groupedWealth = borrowedWealth.reduce((acc: { [key: string]: MobileWealthDTO[] }, item) => {
        let dateKey = 'Unknown';
        if (item.rentDateTo) {
            dateKey = new Date(item.rentDateTo).toLocaleDateString(i18n.language);
        }
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(item);
        return acc;
    }, {});

    // When selecting a building, render BuildingsList full screen (no panels)
    if (selectingBuilding) {
        return (
            <Box sx={{ height: 'calc(100vh - 56px)' }}>
                <BuildingsList
                    onBuildingClick={(building) => {
                        if (building?.buildingId) {
                            recordsApi.insertLog(new InsertLogRecordRequest({
                                action: '(MobileWealthBorrow) Wealth borrow buildingClick: ' + building.buildingId,
                                deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
                            }));
                            setSelectedBuildingId(building.buildingId);
                            setSelectingBuilding(false);
                            setSigning(true);
                        }
                    }}
                />
            </Box>
        );
    }

    return (
        <Box sx={{ height: 'calc(100vh - 56px)' }}>
            <PanelGroup direction="vertical"
                autoSaveId="mobileWealthPanel"
                style={{ height: '100%' }}>
                {/* Top Panel: TabPanels / Signature */}
                <Panel defaultSize={40} minSize={30} style={{ padding: '0px 0px 16px 0px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
                        {!signing && (
                            <TabContext value={'2'}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <Box
                                        sx={{
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1,
                                            background: 'white',
                                            borderBottom: 1,
                                            borderColor: 'divider'
                                        }}
                                    >
                                        <TabList
                                            sx={{
                                                backgroundColor: '#FFC600',
                                                color: 'black',
                                                '& .Mui-selected': {
                                                    color: 'black !important',
                                                    fontWeight: 'bold'
                                                },
                                                '& .MuiTabs-indicator': {
                                                    backgroundColor: 'black'
                                                }
                                            }}
                                            aria-label="mobile-attendance-tabs"
                                        >
                                            <Tab
                                                onClick={() => {
                                                    // @ts-ignore
                                                    if (window.ReactNativeWebView) {
                                                        recordsApi.insertLog(new InsertLogRecordRequest({
                                                            action: '(MobileWealthBorrow) WealthBorrow open scanner click',
                                                            deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
                                                        }));

                                                        // @ts-ignore
                                                        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'open-scanner' }))
                                                    } else {
                                                        enqueueSnackbar(t('ScanningIsOnlyAvailableInMobileApp'), { variant: 'info' });
                                                    }

                                                }} label={<Typography sx={{ fontWeight: 'bold' }}>{t('Scan')}</Typography>} value="1" sx={{ flex: 1 }} />
                                            <Tab label={<Typography sx={{ fontWeight: 'bold' }}>{t('List')}</Typography>} value="2" sx={{ flex: 1 }} />
                                        </TabList>
                                    </Box>
                                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                                        <TabPanel value="1">
                                            <Box display="flex" flexDirection="column" alignItems="center">

                                            </Box>
                                        </TabPanel>
                                        <TabPanel value="2">
                                            <WealthToBorrowList
                                                selectedWealths={borrowedWealth}
                                                onWealthClick={(wealth: MobileWealthDTO | undefined) => {
                                                    if (wealth && !borrowedWealth.find(item => item.wealthId === wealth.wealthId)) {
                                                        recordsApi.insertLog(new InsertLogRecordRequest({
                                                            action: '(MobileWealthBorrow) WealthToBorrowList wealth click: ' + wealth?.wealthId + ' (' + wealth?.code + ')',
                                                            deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
                                                        }));
                                                        setBorrowedWealthActual(
                                                            new MobileWealthDTO({
                                                                wealthId: wealth.wealthId,
                                                                code: wealth.code,
                                                                name: wealth.name,
                                                                imagePreview: wealth.imagePreview,
                                                                borrowedByCurrentEmployee: wealth.borrowedByCurrentEmployee,
                                                                employeeCodeAndName: wealth.employeeCodeAndName,
                                                                buildingCodeAndName: wealth.buildingCodeAndName,
                                                                rentDateFrom: wealth.rentDateFrom,
                                                                rentDateTo: wealth.rentDateTo
                                                            })
                                                        );
                                                        setDetailOpen(true);
                                                    } else {
                                                        recordsApi.insertLog(new InsertLogRecordRequest({
                                                            action: '(MobileWealthBorrow) Trying to add already added wealth: ' + wealth?.wealthId,
                                                            deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
                                                        }));
                                                        enqueueSnackbar(t('WealthAlreadyAdded'), { variant: 'info' });
                                                    }
                                                }}
                                            />
                                        </TabPanel>
                                    </Box>
                                </Box>
                            </TabContext>
                        )}
                        {signing && (
                            <Stack m={1} mb={2} spacing={2} px={4} alignItems="center">
                                {/* <LocalizationProviderWithLanguages>
                                    <DatePicker
                                        label={t('StartDate')}
                                        value={tempDate}
                                        onChange={(date) => {
                                            setTempDate(date);
                                        }}
                                        renderInput={(params) =>
                                            <TextField
                                                placeholder={t('StartDate')}
                                                fullWidth
                                                size='small'
                                                {...params}
                                                sx={{ background: 'white', borderRadius: 4, float: 'right' }} />}
                                    />
                                </LocalizationProviderWithLanguages> */}
                                <Typography sx={{ fontSize: 19, fontWeight: '500' }}>{t('SignThePropertyTakeover')}</Typography>
                                <Box>
                                    <SignatureComponent
                                        ref={signRef}
                                        style={{
                                            border: '1px solid #C3C3C3',
                                            borderRadius: '6px',
                                            maxWidth: isMobileS ? '300px' : isMobileM ? '300px' : isMobileL ? '400px' : isTablet ? '500px' : '100%',
                                        }}
                                    />
                                </Box>
                                <Stack direction="row" spacing={1} width="100%">
                                    <Button
                                        startIcon={<ReplayIcon />}
                                        sx={{ width: '50%', background: 'black !important', color: 'white', fontWeight: 'bold' }}
                                        variant="contained"
                                        onClick={() => {
                                            signRef.current?.clear();
                                        }}
                                    >
                                        {t('Retry')}
                                    </Button>
                                    <Button startIcon={<CheckIcon />} sx={{ width: '50%' }} variant="contained" onClick={borrow}>
                                        {t('sign')}
                                    </Button>
                                </Stack>
                            </Stack>
                        )}
                    </Box>
                </Panel>
                {/* Draggable Handle */}
                <PanelResizeHandle
                    hitAreaMargins={{ coarse: 20, fine: 20 }}
                    style={{
                        background: '#FFC600',
                        height: '6px',
                    }}
                />
                {/* Bottom Panel: Wealth to Borrow List */}
                <Panel defaultSize={60} minSize={20}>
                    <Box sx={{ mx: 2, mb: 2, display: 'flex', flexDirection: 'column', pt: 2, height: '100%' }}>
                        <Box
                            sx={{
                                background: 'white',
                                borderRadius: 4,
                                boxShadow: 4,
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                overflow: 'hidden'
                            }}
                        >
                            <Typography
                                sx={{ textAlign: 'center', borderBottom: '1px solid', borderColor: 'divider', fontSize: 20, fontWeight: '500' }}
                            >
                                {t('WealthToBorrow')}
                            </Typography>
                            <Box
                                sx={{
                                    width: '100%',
                                    overflowY: 'auto',
                                    mt: 1,
                                    flex: 1,
                                    px: 2
                                }}
                            >
                                {/* {Object.entries(groupedWealth).map(([date, items]) => (
                                    <Box key={date} mb={2}>
                                        <Typography sx={{ textAlign: 'center', fontSize: 16, fontWeight: 'bold' }}>
                                            {t('to')} {date}
                                        </Typography> */}
                                {borrowedWealth.map((item) => (
                                    <Box
                                        key={item.wealthId}
                                        width="100%"
                                        mb={1}
                                        border="1px solid #C3C3C3"
                                        px={1}
                                        display="flex"
                                        justifyContent="space-between"
                                        borderRadius={6}
                                    >
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography sx={{ fontSize: 14, fontWeight: 'bold' }}>{item.code}</Typography>
                                            <Typography sx={{ lineHeight: 1, fontSize: 14 }}>{item.name}</Typography>
                                        </Box>
                                        <IconButton
                                            onClick={() => {
                                                recordsApi.insertLog(new InsertLogRecordRequest({
                                                    action: '(MobileWealthBorrow) WealthToBorrowList delete wealth: ' + item.wealthId,
                                                    deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
                                                }));
                                                setBorrowedWealth(borrowedWealth.filter((q) => q.wealthId !== item.wealthId));
                                            }}
                                            color="error"
                                        >
                                            <DeleteOutline />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                            {/* ))}
                            </Box> */}
                        </Box>
                        {!signing && (
                            <Button
                                fullWidth
                                variant="contained"
                                sx={{ mt: 2 }}
                                disabled={borrowedWealth.length === 0}
                                onClick={() => {
                                    setSelectingBuilding(true);
                                    setModuleButton(
                                        <IconButton
                                            onClick={() => {
                                                setSigning(false);
                                                setSelectingBuilding(false);
                                                setModuleButton(null);
                                            }}
                                        >
                                            <ArrowBackIosNew />
                                        </IconButton>
                                    );
                                }}
                            >
                                {t('Continue')}
                            </Button>
                        )}
                    </Box>
                </Panel>
            </PanelGroup>
            <MobileWealthDetailDialog
                open={detailOpen}
                wealth={borrowedWealthActual}
                onClose={(e: boolean, rentDateTo: Date | null) => {
                    setData('');
                    if (e) {
                        recordsApi.insertLog(new InsertLogRecordRequest({
                            action: '(MobileWealthBorrow) WealthDetailDialog borrow click ' + borrowedWealthActual?.wealthId,
                            deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
                        }));
                        setBorrowedWealth([...borrowedWealth, {
                            ...borrowedWealthActual,
                            rentDateTo: rentDateTo ?? borrowedWealthActual?.rentDateTo
                        } as MobileWealthDTO]);
                        setBorrowedWealthActual(null);
                        setDetailOpen(false);
                    } else {
                        setDetailOpen(false);
                    }
                }}
            />
        </Box>
    );
}
